import { REDIRECT_URL_KEY } from "../constants/app.constant";

export const AUTHENTICATION_RETURN_TO_FALLBACK = "/home";

const VALIDATION_ORIGIN = "https://indicafix.invalid";

const containsControlCharacter = (value: string) =>
  [...value].some((character) => {
    const code = character.charCodeAt(0);
    return code <= 0x1f || code === 0x7f;
  });

export type AuthenticationReturnIntent = string;

export type AuthenticationReturnLocation = {
  href?: unknown;
  hash?: unknown;
  pathname: string;
  search?: unknown;
  searchStr?: unknown;
};

export function isSafeAuthenticationReturnTo(
  value: unknown,
): value is AuthenticationReturnIntent {
  if (
    typeof value !== "string" ||
    value.length === 0 ||
    value !== value.trim() ||
    !value.startsWith("/") ||
    value.startsWith("//") ||
    value.startsWith("/\\") ||
    containsControlCharacter(value)
  ) {
    return false;
  }

  try {
    return new URL(value, VALIDATION_ORIGIN).origin === VALIDATION_ORIGIN;
  } catch {
    return false;
  }
}

export function resolveAuthenticationReturnTo(
  value: unknown,
): AuthenticationReturnIntent {
  return isSafeAuthenticationReturnTo(value)
    ? value
    : AUTHENTICATION_RETURN_TO_FALLBACK;
}

const withPrefix = (value: unknown, prefix: string) => {
  if (typeof value !== "string" || value.length === 0) return "";
  return value.startsWith(prefix) ? value : `${prefix}${value}`;
};

export function getAuthenticationReturnToFromLocation(
  location: AuthenticationReturnLocation,
): AuthenticationReturnIntent {
  const href = typeof location.href === "string" ? location.href : undefined;
  if (href) return resolveAuthenticationReturnTo(href);

  const search =
    typeof location.searchStr === "string"
      ? location.searchStr
      : location.search;
  const hash = withPrefix(location.hash, "#");
  const pathname = location.pathname;

  return resolveAuthenticationReturnTo(
    `${pathname}${withPrefix(search, "?")}${hash}`,
  );
}

export function getAuthenticationReturnToFromSearch(
  search: string | undefined,
): AuthenticationReturnIntent {
  return resolveAuthenticationReturnTo(
    search ? new URLSearchParams(search).get(REDIRECT_URL_KEY) : undefined,
  );
}

export function buildAuthenticationPath(
  pathname: string,
  returnTo: unknown,
): string {
  const params = new URLSearchParams({
    [REDIRECT_URL_KEY]: resolveAuthenticationReturnTo(returnTo),
  });
  return `${pathname}?${params.toString()}`;
}
